import { useEffect, useRef, useState, type ReactElement } from 'react';
import { preencher } from '@zenda/kernel/variaveis';
import type { QuickReply, Uuid } from '@zenda/api-contract';
import { Icon } from './Icon';
import { useQuickReplies } from '../lib/queries';
import { useT } from '../lib/i18n';

/**
 * O menu de mensagens prontas do campo de resposta.
 *
 * Mostra as gerais e as do medico DESTA conversa. As de outro medico ficam de
 * fora: "A Dra. Helena atende as sextas" numa conversa do Dr. Rafael e o tipo de
 * erro que so se descobre depois de enviado.
 */
export function RespostasProntas({
  clientId,
  variaveis,
  onEscolher,
}: {
  readonly clientId: Uuid;
  readonly variaveis: Readonly<Record<string, string | null>>;
  readonly onEscolher: (texto: string) => void;
}): ReactElement | null {
  const t = useT();
  const [aberto, setAberto] = useState(false);
  const caixaRef = useRef<HTMLDivElement | null>(null);
  const respostas = useQuickReplies();

  // Clique fora fecha. Sem isto o menu fica aberto por cima do historico
  // enquanto ela tenta ler a conversa para decidir o que responder.
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent): void => {
      if (caixaRef.current !== null && !caixaRef.current.contains(e.target as Node)) {
        setAberto(false);
      }
    };
    const esc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setAberto(false);
    };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', esc);
    };
  }, [aberto]);

  const lista = (respostas.data ?? []).filter(
    (q) => q.clientId === null || q.clientId === clientId,
  );

  function escolher(q: QuickReply): void {
    // O que da para preencher entra pronto; o que falta continua como marcador,
    // e a barra de composicao pinta de vermelho para ela apagar e escrever.
    onEscolher(preencher(q.body, variaveis).texto);
    setAberto(false);
  }

  return (
    <div className="rp" ref={caixaRef}>
      <button
        type="button"
        className={`rp__botao${aberto ? ' is-on' : ''}`}
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
        aria-label={t.ui.mensagensProntas}
      >
        <Icon name="sparkle" size={17} />
      </button>

      {aberto ? (
        <div className="rp__menu" role="menu">
          {lista.length === 0 ? (
            <p className="rp__vazio">{t.ui.semMensagensProntas}</p>
          ) : (
            lista.map((q) => {
              const r = preencher(q.body, variaveis);
              return (
                <button
                  key={q.id}
                  type="button"
                  className="rp__item"
                  role="menuitem"
                  onClick={() => escolher(q)}
                >
                  <span className="rp__item-topo">
                    <span className="rp__item-titulo">{q.title}</span>
                    {r.faltando.length > 0 ? (
                      <span className="rp__item-falta">
                        <Icon name="alert" size={11} />
                        {t.ui.faltaPreencher}
                      </span>
                    ) : null}
                  </span>
                  <span className="rp__item-previa">{r.texto}</span>
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}
